import { UseMutationResult, useMutation } from '@tanstack/react-query'
import { apiClient } from 'api/apiClient/apiClient'
import { queryClient } from 'helpers/queryClient'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import getLogger from 'utils/log/log'
import { CREATE_DATA_POINTS_ENDPOINT, EDIT_TIME_SERIES_DATASET_QUERY_KEY } from './timeseries.constants'
import { EditTimeseriesResponseSchema, EditTimeseriesResult, EditTimeseriesVariables } from './timeseries.types'

const log = getLogger('timeseries.api')

export function useTimeseriesMutation(): UseMutationResult<
  EditTimeseriesResult,
  unknown,
  EditTimeseriesVariables,
  unknown
> {
  const { t } = useTranslation()
  const { activeSystem } = useAuth()
  return useMutation({
    // `created_at` is never sent. The endpoint accepts it for backfilling a point at its true origin
    // time, but a point carrying the timestamp it was read with loses the store's version comparison
    // and is silently discarded — an edit that reports success and never becomes the visible value.
    mutationFn: async ({ dataPoints, aggregateDuration }: EditTimeseriesVariables) => {
      // Declaring what a row represents lets the endpoint write the value to every point inside it,
      // so an hourly cell over a quarter-hourly series stops moving by a quarter of what was typed.
      // Where reads are unaggregated the endpoint ignores it and writes the single point named, so
      // this is safe to send everywhere; only an interval that contradicts the read is rejected.
      const response = await apiClient<unknown>(CREATE_DATA_POINTS_ENDPOINT, {
        method: 'POST',
        data: { data_points: dataPoints, system: activeSystem?.name, aggregate_duration: aggregateDuration },
      })

      const parsed = EditTimeseriesResponseSchema.safeParse(response)
      if (parsed.success) {
        return { status: 'read', ...parsed.data } as const
      }

      log.warn('Unexpected create_data_points response shape', parsed.error)
      return { status: 'unreadable' } as const
    },
    onError: () => {
      alertStore.error(t('Failed to edit time series'))
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: [EDIT_TIME_SERIES_DATASET_QUERY_KEY] })

      if (result.status === 'unreadable') {
        alertStore.warning(
          t('The save was accepted, but the result could not be confirmed. Reload to check the new values.')
        )
        return
      }

      const editedValues = result.nr_of_edited_values ?? result.nr_of_data_points

      if (editedValues === 0) {
        alertStore.info(t('No data points were changed.'))
        return
      }

      if (result.queued_job_id === null) {
        alertStore.success(
          t('Successfully edited {{count}} data points.', {
            count: editedValues,
          })
        )
        return
      }

      alertStore.success(
        t(
          'Successfully edited {{count}} data points. An outcome calculation has been queued, and the production outcome will be updated shortly.',
          {
            count: editedValues,
          }
        )
      )
    },
  })
}
